import { PaginatedResponse } from '@/core/models/PaginatedResponse';
import { TranslatedProperty } from '@/core/models/TranslatedProperty';
import { StoreState } from '@/core/models/store.interface';
import { LocationQuery } from 'vue-router';
import { ActionContext, Module } from 'vuex';
import { DocumentSettingState } from './document-settings/document-setting.store';
import { Employer } from './employer.interface';
import { EmployerService } from './employer.service';
import { PaymentSystemJobFunctionState } from './payment-system-job-functions/payment-system-job-function.store';
import { PaymentSystemPerformanceCodeState } from './payment-system-performance-codes/payment-system-performance-code.store';
import { PaymentSystemTeamState } from './payment-system-teams/payment-system-team.store';
import { PerformanceCodeTriggerState } from './performance-code-triggers/performance-code-trigger.store';
import { TermsOfEmploymentState } from './terms-of-employments/terms-of-employment.store';
import workMaterialCategory, { WorkMaterialCategoryState } from './work-material-categories/work-material-category.store';
import workMaterialType, { WorkMaterialTypeState } from './work-material-types/work-material-type.store';

const service = new EmployerService();

export interface EmployerState {
   employerOptions: Array<Employer>;
   employersOverview?: PaginatedResponse<Employer>;
   employer?: Employer;
   loadingEmployer: boolean;

   documentSetting?: DocumentSettingState;
   paymentSystemJobFunction?: PaymentSystemJobFunctionState;
   paymentSystemPerformanceCode?: PaymentSystemPerformanceCodeState;
   paymentSystemTeam?: PaymentSystemTeamState;
   performanceCodeTrigger?: PerformanceCodeTriggerState;
   workMaterialCategory?: WorkMaterialCategoryState;
   workMaterialType?: WorkMaterialTypeState;
   termsOfEmployment?: TermsOfEmploymentState;
}

const EmployerStore: Module<EmployerState, StoreState> = {
   namespaced: true,
   state: { employerOptions: [], loadingEmployer: false },
   getters: {
      employerOptions: (state: EmployerState) => state.employerOptions,
      employersOverview: (state: EmployerState) => state.employersOverview,
      employer: (state: EmployerState) => state.employer,
      loadingEmployer: (state: EmployerState) => state.loadingEmployer,
   },
   mutations: {
      SET_EMPLOYER_OPTIONS(state: EmployerState, employersOptions: Array<Employer>) {
         state.employerOptions = employersOptions;
      },
      SET_EMPLOYERS_OVERVIEW(state: EmployerState, employersOverview: PaginatedResponse<Employer>) {
         state.employersOverview = employersOverview;
      },
      SET_EMPLOYER(state: EmployerState, employer?: Employer) {
         state.employer = employer;
         const index = state.employerOptions?.findIndex((o) => o.id === employer?.id);
         if (index > -1) {
            if (employer) state.employerOptions[index] = employer;
            else state.employerOptions.splice(index, 1);
         }
      },
      ADD_EMPLOYER(state: EmployerState, employer: Employer) {
         state.employersOverview?.data?.push(employer);
      },
      SET_LOADING_EMPLOYER(state: EmployerState, loading: boolean) {
         state.loadingEmployer = loading;
      },
      SET_EMPLOYER_SIGNATURE(state: EmployerState, signaturePath: string) {
         if (!state.employer) return;
         state.employer.contract_signature_path = signaturePath;
      },
      SET_EMPLOYER_LOGO(state: EmployerState, logoPath: string) {
         if (!state.employer) return;
         state.employer.logo_path = logoPath;
      },
   },
   actions: {
      async getEmployerOptions(context: ActionContext<EmployerState, any>, query: LocationQuery) {
         return service.getEmployers({ page: '1', per_page: '999', ...query }).then((response) => {
            context.commit('SET_EMPLOYER_OPTIONS', response?.data || []);
            return response?.data || [];
         });
      },
      async getEmployers(context: ActionContext<EmployerState, any>, query: LocationQuery) {
         return service.getEmployers(query).then((response) => {
            context.commit('SET_EMPLOYERS_OVERVIEW', response);
            return response;
         });
      },
      async getEmployer(context: ActionContext<EmployerState, any>, payload: { employerId: number; query?: LocationQuery }) {
         context.commit('SET_LOADING_EMPLOYER', true);
         return await service.getEmployer(payload.employerId, payload.query).then((response) => {
            context.commit('SET_EMPLOYER', response);
            context.commit('SET_LOADING_EMPLOYER', false);
            return response;
         });
      },
      async createEmployer(context: ActionContext<EmployerState, any>, employer: Employer) {
         return await service.postEmployer(employer).then((response) => {
            context.commit('ADD_EMPLOYER', response);
            return response;
         });
      },
      async changeEmployer(context: ActionContext<EmployerState, any>, payload: { employerId: number; employer: Employer }) {
         return await service.putEmployer(payload.employerId, payload.employer).then((response) => {
            context.commit('SET_EMPLOYER', response);
            return response;
         });
      },

      async uploadSignature(context: ActionContext<EmployerState, any>, payload: { employerId: number; file: File }) {
         const response = await service.uploadSignature(payload.employerId, payload.file);
         context.commit('SET_EMPLOYER_SIGNATURE', response.path);
         return response.path;
      },

      async uploadLogo(context: any, payload: { employerId: number; logo: string }) {
         const response = await service.uploadLogo(payload.employerId, payload.logo);
         context.commit('SET_EMPLOYER_LOGO', response.path);
         return response.path;
      },

      async updatePrivacyPolicy(context, payload: { employerId: number; privacyPolicy: TranslatedProperty }) {
         const newEmployer = { ...context.state.employer, privacy_policy_url: payload.privacyPolicy };
         return context.dispatch('changeEmployer', { employerId: payload.employerId, employer: newEmployer });
      },
   },
   modules: {
      workMaterialType,
      workMaterialCategory,
   },
};

export default EmployerStore;
